'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { notFound } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { markRewardsUnlocked, readRewardsUnlocked } from '@/components/rewards/rewards-unlocked';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { checkGate, getProviderReady, subscribeProviderReady } from '@/lib/analytics';
import { consumeLogoutIntent } from '@/lib/auth/logout-intent';
import { useAppRouter } from '@/lib/network/use-app-router';
import { showToast } from '@/lib/ui/toast';

/** Rewards retains its feature availability check and authenticated-session requirement. */
export function RewardsGate(t0) {
  const $ = _c(11);
  const {
    children
  } = t0;
  const providerReady = useSyncExternalStore(subscribeProviderReady, getProviderReady, _temp);
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const router = useAppRouter();
  const t = useTranslations("rewards.join");
  const [passesFlag, setPassesFlag] = useState(null);
  let t1;
  let t2;
  if ($[0] !== providerReady) {
    t1 = () => {
      if (readRewardsUnlocked()) {
        setPassesFlag(true);
        return;
      }
      if (!providerReady) {
        return;
      }
      const live = checkGate("rewards_platform");
      if (live) {
        markRewardsUnlocked();
      }
      setPassesFlag(live);
    };
    t2 = [providerReady];
    $[0] = providerReady;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const mustRedirect = passesFlag === true && !isLoading && !isAuthenticated;
  const redirectHandledRef = useRef(false);
  let t3;
  let t4;
  if ($[3] !== mustRedirect || $[4] !== router || $[5] !== t) {
    t3 = () => {
      if (!mustRedirect || redirectHandledRef.current) {
        return;
      }
      redirectHandledRef.current = true;
      if (consumeLogoutIntent()) {
        onEvent("rewards_gate_bounce", {
          reason: "logout"
        });
        router.replace("/");
        return;
      }
      onEvent("rewards_gate_bounce", {
        reason: "signed_out"
      });
      showToast({
        id: TOAST_IDS.REWARDS_SIGN_IN_REQUIRED,
        message: t("signInRequired"),
        variant: "error"
      });
      router.replace("/markets");
    };
    t4 = [mustRedirect, router, t];
    $[3] = mustRedirect;
    $[4] = router;
    $[5] = t;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  if (passesFlag === null) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <RewardsGateLoading />;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (!passesFlag) {
    notFound();
  }
  if (isLoading) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <RewardsGateLoading />;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  if (!isAuthenticated) {
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <RewardsGateLoading />;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  return children;
}

/** Full-page spinner shown while {@link RewardsGate} resolves its flag/auth
 *  bootstrap — the app's canonical loader (matches `auth/callback/loading.tsx`)
 *  on the rewards canvas so the loader → dashboard swap doesn't flash. */
function _temp() {
  return false;
}
function RewardsGateLoading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <main data-testid="rewards-gate-loading" className="flex min-h-dvh items-center justify-center bg-background-secondary"><Icon icon={Loading03Icon} className="size-10 animate-spin text-foreground-secondary motion-reduce:animate-none" aria-hidden={true} /></main>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}